import { useState, FormEvent } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const trustPoints = [
  {
    icon: "ri-shield-cross-line",
    title: "BUILT FOR HEALTH & CLINICAL",
    desc: "Every system is designed around the compliance requirements, patient sensitivities, and operational realities of health and wellness businesses.",
  },
  {
    icon: "ri-bar-chart-box-line",
    title: "DESIGNED FOR MEASURABLE GROWTH",
    desc: "We don't deploy tools—we deploy trackable infrastructure. Every system ships with performance benchmarks and reporting.",
  },
  {
    icon: "ri-stack-line",
    title: "STRUCTURED, SCALABLE SYSTEMS",
    desc: "Modular architecture that grows with your business. Add locations, services, and channels without rebuilding from scratch.",
  },
  {
    icon: "ri-timer-flash-line",
    title: "FAST IMPLEMENTATION TIMELINES",
    desc: "From strategy call to live system in 14 days. No 6-month dev cycles—just rapid, precision deployment.",
  },
];

const processSteps = [
  { number: "01", name: "AUDIT", desc: "Deep-dive into your current patient acquisition, scheduling, and retention workflows." },
  { number: "02", name: "SYSTEM DESIGN", desc: "Custom infrastructure blueprint tailored to your business model and growth targets." },
  { number: "03", name: "DEPLOYMENT", desc: "Live system in 14 days with full team training and zero operational disruption." },
  { number: "04", name: "OPTIMIZATION", desc: "Ongoing performance monitoring with monthly optimization sprints." },
];

const industries = ["Med Spa", "Clinical Practice", "Wellness Brand", "Physical Therapy", "Dental Practice", "Other Health Business"];
const leadVolumes = ["Under 50/month", "50–200/month", "200–500/month", "500–1,000/month", "1,000+/month"];
const challenges = [
  "Missed calls & inquiries",
  "Slow response times",
  "Patient drop-off",
  "Administrative overload",
  "Low retention rates",
  "Inefficient scheduling",
  "Other",
];

type FormStatus = "idle" | "submitting" | "success" | "error";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    businessName: "",
    email: "",
    phone: "",
    industry: "",
    leadVolume: "",
    challenge: "",
  });
  const [status, setStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "challenge") setCharCount(value.length);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;

    setStatus("submitting");

    const params = new URLSearchParams();
    params.append("name", formData.name);
    params.append("businessName", formData.businessName);
    params.append("email", formData.email);
    params.append("phone", formData.phone);
    params.append("industry", formData.industry);
    params.append("leadVolume", formData.leadVolume);
    params.append("challenge", formData.challenge);

    try {
      const res = await fetch("https://readdy.ai/api/form/d73a0bkuf8iffoai1nmg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      if (res.ok) {
        setStatus("success");
        setFormData({ name: "", businessName: "", email: "", phone: "", industry: "", leadVolume: "", challenge: "" });
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const inputBase =
    "w-full bg-[#111] border border-white/10 text-white text-sm px-4 py-3.5 outline-none transition-all duration-200 focus:border-[#FFD100] focus:bg-[#151515] placeholder:text-white/25";
  const labelBase = "block text-xs font-bold tracking-[2px] text-[#E5E5E5] mb-2";

  return (
    <div className="bg-black min-h-screen" style={{ fontFamily: "Arial, sans-serif" }}>
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative bg-black overflow-hidden pt-20">
        {/* Axis grid */}
        <div className="absolute inset-0 pointer-events-none">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" className="opacity-[0.07]">
            <defs>
              <pattern id="contactGrid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#FFD100" strokeWidth="0.6" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#contactGrid)" />
          </svg>
          <div className="absolute top-[55%] left-0 right-0 h-px bg-[#FFD100]/15" />
          <div className="absolute top-0 bottom-0 right-[25%] w-px bg-[#FFD100]/10" />
          <div className="absolute top-[55%] right-[25%] w-3 h-3 border border-[#FFD100]/50 -translate-x-1.5 -translate-y-1.5 rotate-45" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-16 py-20 md:py-28 text-center">
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="block w-12 h-px bg-[#FFD100]" />
            <span className="text-[#FFD100] text-xs tracking-[4px] font-bold uppercase">CONTACT</span>
            <span className="block w-12 h-px bg-[#FFD100]" />
          </div>
          <h1
            className="text-white font-black uppercase leading-none mb-7"
            style={{
              fontFamily: "Arial, sans-serif",
              fontSize: "clamp(2.4rem, 5.5vw, 5rem)",
              letterSpacing: "-1px",
              lineHeight: "1.05",
            }}
          >
            BUILD YOUR PATIENT
            <br />
            <span className="text-[#FFD100]">ACQUISITION SYSTEM</span>
          </h1>
          <p
            className="text-[#E5E5E5] text-base md:text-lg max-w-2xl mx-auto"
            style={{ lineHeight: "1.75" }}
          >
            Book a strategy call and we&apos;ll show you exactly where your growth is
            leaking—and how to fix it.
          </p>
        </div>
      </section>

      {/* ── MAIN CONTENT: FORM + TRUST ── */}
      <section className="py-12 md:py-16 px-6 md:px-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">

          {/* ── FORM ── */}
          <div className="lg:col-span-3">
            <div className="mb-8">
              <span className="text-[#FFD100] text-xs tracking-[4px] font-bold block mb-3">
                GROWTH AUDIT REQUEST
              </span>
              <h2
                className="text-white font-black uppercase text-2xl md:text-3xl leading-tight"
                style={{ fontFamily: "Arial, sans-serif" }}
              >
                TELL US ABOUT YOUR BUSINESS
              </h2>
              <div className="w-16 h-0.5 bg-[#FFD100] mt-5" />
            </div>

            {status === "success" ? (
              <div className="bg-[#1A1A1A] border border-[#FFD100]/40 p-10 flex flex-col items-center text-center gap-6">
                <div className="w-16 h-16 flex items-center justify-center border-2 border-[#FFD100]">
                  <i className="ri-check-line text-[#FFD100] text-3xl" />
                </div>
                <div>
                  <h3
                    className="text-white font-black uppercase text-xl tracking-[1px] mb-3"
                    style={{ fontFamily: "Arial, sans-serif" }}
                  >
                    REQUEST RECEIVED
                  </h3>
                  <p className="text-[#E5E5E5] text-sm leading-relaxed max-w-md" style={{ lineHeight: "1.8" }}>
                    Our team will review your submission and reach out within 24 hours to
                    schedule your growth audit. Check your inbox.
                  </p>
                </div>
                <button
                  onClick={() => setStatus("idle")}
                  className="border border-[#FFD100]/40 text-[#FFD100] text-xs font-black tracking-[2px] px-8 py-3 hover:bg-[#FFD100] hover:text-black transition-all duration-200 cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  SUBMIT ANOTHER REQUEST
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                {/* Row 1: Name + Business Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelBase}>FULL NAME *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Dr. Sarah Mitchell"
                      className={inputBase}
                      style={{ fontFamily: "Arial, sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className={labelBase}>BUSINESS NAME *</label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      required
                      placeholder="Luxe Med Spa"
                      className={inputBase}
                      style={{ fontFamily: "Arial, sans-serif" }}
                    />
                  </div>
                </div>

                {/* Row 2: Email + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelBase}>EMAIL ADDRESS *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="sarah@luxemedspa.com"
                      className={inputBase}
                      style={{ fontFamily: "Arial, sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className={labelBase}>PHONE NUMBER</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className={inputBase}
                      style={{ fontFamily: "Arial, sans-serif" }}
                    />
                  </div>
                </div>

                {/* Row 3: Industry + Lead Volume */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelBase}>INDUSTRY *</label>
                    <div className="relative">
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        required
                        className={`${inputBase} appearance-none cursor-pointer pr-10`}
                        style={{ fontFamily: "Arial, sans-serif" }}
                      >
                        <option value="" disabled>Select industry</option>
                        {industries.map((ind) => (
                          <option key={ind} value={ind} className="bg-[#111]">{ind}</option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line text-[#FFD100] text-lg" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className={labelBase}>MONTHLY LEAD VOLUME *</label>
                    <div className="relative">
                      <select
                        name="leadVolume"
                        value={formData.leadVolume}
                        onChange={handleChange}
                        required
                        className={`${inputBase} appearance-none cursor-pointer pr-10`}
                        style={{ fontFamily: "Arial, sans-serif" }}
                      >
                        <option value="" disabled>Select volume</option>
                        {leadVolumes.map((v) => (
                          <option key={v} value={v} className="bg-[#111]">{v}</option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line text-[#FFD100] text-lg" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Primary Challenge */}
                <div>
                  <label className={labelBase}>PRIMARY CHALLENGE *</label>
                  <div className="relative">
                    <select
                      name="challenge"
                      value={formData.challenge}
                      onChange={handleChange}
                      required
                      className={`${inputBase} appearance-none cursor-pointer pr-10`}
                      style={{ fontFamily: "Arial, sans-serif" }}
                    >
                      <option value="" disabled>Select your biggest challenge</option>
                      {challenges.map((c) => (
                        <option key={c} value={c} className="bg-[#111]">{c}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-[#FFD100] text-lg" />
                    </div>
                  </div>
                </div>

                {/* Error state */}
                {status === "error" && (
                  <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 flex items-center gap-3">
                    <i className="ri-error-warning-line text-red-400 text-base" />
                    <span className="text-red-400 text-xs tracking-[1px]" style={{ fontFamily: "Arial, sans-serif" }}>
                      Something went wrong. Please try again or email us directly.
                    </span>
                  </div>
                )}

                {/* Submit */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full bg-[#FFD100] text-black text-xs font-black tracking-[3px] py-5 cursor-pointer whitespace-nowrap hover:bg-white transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                    style={{ fontFamily: "Arial, sans-serif" }}
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base" />
                        SENDING REQUEST...
                      </>
                    ) : (
                      "BOOK YOUR GROWTH AUDIT"
                    )}
                  </button>
                  <p className="text-white/25 text-xs text-center mt-3 tracking-[1px]" style={{ fontFamily: "Arial, sans-serif" }}>
                    We respond within 24 hours. No spam. No obligations.
                  </p>
                </div>
              </form>
            )}
          </div>

          {/* ── RIGHT SIDEBAR ── */}
          <div className="lg:col-span-2 flex flex-col gap-10">

            {/* Trust Points */}
            <div>
              <span className="text-[#FFD100] text-xs tracking-[4px] font-bold block mb-6">WHY SOLAINVESTAI</span>
              <div className="flex flex-col gap-6">
                {trustPoints.map((tp, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <div className="w-10 h-10 flex-shrink-0 border border-[#FFD100]/40 flex items-center justify-center">
                      <i className={`${tp.icon} text-[#FFD100] text-base`} />
                    </div>
                    <div>
                      <h3
                        className="text-white font-black text-xs tracking-[2px] mb-1.5"
                        style={{ fontFamily: "Arial, sans-serif" }}
                      >
                        {tp.title}
                      </h3>
                      <p className="text-[#E5E5E5] text-xs leading-relaxed" style={{ lineHeight: "1.8" }}>
                        {tp.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Process Preview */}
            <div className="bg-[#1A1A1A] border-t-2 border-[#FFD100] p-7">
              <span className="text-[#FFD100] text-xs tracking-[4px] font-bold block mb-6">THE PROCESS</span>
              <div className="flex flex-col gap-0">
                {processSteps.map((step, i) => (
                  <div key={i} className="flex gap-4 relative">
                    {/* Vertical connector line */}
                    {i < processSteps.length - 1 && (
                      <div className="absolute left-[18px] top-8 bottom-0 w-px bg-[#FFD100]/20" />
                    )}
                    {/* Step number node */}
                    <div className="w-9 h-9 flex-shrink-0 bg-black border border-[#FFD100]/40 flex items-center justify-center z-10 relative">
                      <span
                        className="text-[#FFD100] font-black text-[10px] tracking-[1px]"
                        style={{ fontFamily: "Arial, sans-serif" }}
                      >
                        {step.number}
                      </span>
                    </div>
                    {/* Content */}
                    <div className={`pb-6 ${i === processSteps.length - 1 ? "pb-0" : ""}`}>
                      <h4
                        className="text-white font-black text-xs tracking-[2px] mb-1"
                        style={{ fontFamily: "Arial, sans-serif" }}
                      >
                        {step.name}
                      </h4>
                      <p className="text-[#E5E5E5] text-xs leading-relaxed" style={{ lineHeight: "1.8" }}>
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Direct contact */}
            <div className="border border-white/10 p-6 flex flex-col gap-4">
              <span className="text-[#FFD100] text-xs tracking-[4px] font-bold">DIRECT CONTACT</span>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center border border-white/10">
                  <i className="ri-mail-line text-[#FFD100] text-sm" />
                </div>
                <a
                  href="mailto:contact@solainvestai.com"
                  className="text-[#E5E5E5] text-sm hover:text-[#FFD100] transition-colors duration-200"
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  contact@solainvestai.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center border border-white/10">
                  <i className="ri-phone-line text-[#FFD100] text-sm" />
                </div>
                <a
                  href="tel:+15551234567"
                  className="text-[#E5E5E5] text-sm hover:text-[#FFD100] transition-colors duration-200"
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  +1 (555) 123-4567
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA STRIP ── */}
      <section className="bg-[#FFD100] py-8 px-6 md:px-16">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p
            className="text-black font-black uppercase text-sm tracking-[2px] text-center sm:text-left"
            style={{ fontFamily: "Arial, sans-serif" }}
          >
            STOP LOSING PATIENTS. START BUILDING SYSTEMS.
          </p>
          <a
            href="#top"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="bg-black text-[#FFD100] text-xs font-black tracking-[3px] px-8 py-3 cursor-pointer whitespace-nowrap hover:bg-white hover:text-black transition-all duration-200 flex-shrink-0"
            style={{ fontFamily: "Arial, sans-serif" }}
          >
            BOOK YOUR AUDIT
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
